import { __, compose, prop, propOr } from 'ramda';

import { compact, date, eq, path } from '../../../../helpers/compact';
import { MAPPING_INNO_STATUS } from '../../../../helpers/filters';
import {
  MAPPING_CLASS_CATEGORIES,
  MAPPING_CLASS_THEMATICS,
} from '../../../../helpers/pushs';
import { DocumentType } from '../../../../types/document';
import { CATEGORY, CLASSIFICATION, FILTER } from '../../../../types/filters';
import { toImageFromProp } from '../../fields/image';
import { toTextFromProp } from '../../fields/text';
import {
  regionTypeDefault,
  toCategoryType,
  toExpertiseType,
  toRegionType,
  toThematicType,
} from '../../mapping';
import { toRegionFilterType } from '../../mapping/region';
import { relatedPushs } from '../../partials/related-push';
import { commonSlices, processSlices } from '../../slices';
import { deprecatedPageEntity } from './internal';

/**
 * @typedef  {Object} PrismicArticle
 * @property {PrismicImage} image_src
 * @property {Array} title
 * @property {string} subtitle
 * @property {string} category
 * @property {string} thematic
 * @property {string} expertise
 * @property {string} cta
 * @property {string} author
 * @property {string} publishStart
 * @property {string} publishEnd
 * @property {Array} body3
 */

/**
 * @typedef {DocumentSchema} ArticleSchema
 * @property {string} title
 * @property {string} subtitle
 * @property {string} category
 * @property {string} thematic
 * @property {string} expertise
 * @property {string} author
 * @property {string} publishStart
 * @property {string} publishEnd
 * @property {Image} illustration
 * @property {Slice[]} body
 */

const getFilter = (p) => compose(prop(p), prop(0), prop('filters'));
const getMapping = (p, data, mapping) => prop(getFilter(p)(data), mapping);

const getCategoryClassName = (data) =>
  prop(prop('category', data), MAPPING_CLASS_CATEGORIES) || '';
const getThematicClassName = (p, data) =>
  getMapping(p, data, MAPPING_CLASS_THEMATICS) || '';

const getSheet = (data, innovation = null) =>
  compact({
    sheetCover: toImageFromProp('fiche_image', data),
    summary: prop('fiche_summary', data),
    aboutUs: prop('fiche_about_us', data),
    keyPoints: prop('fiche_key_points', data),
    status:
      innovation && prop(prop('fiche_inno_status', data), MAPPING_INNO_STATUS),
  });

const processArticleProps = (data) => {
  const category = toCategoryType(path('category', data));
  const process = prop(category, {
    [CATEGORY.MISCONCEPTION]: () => ({
      timeToRead: '2 min',
    }),
    [CATEGORY.EVENT]: () => ({
      timeToRead: '2 min',
      startDateTime: date(
        prop('start_date_time', data),
        'YYYY-MM-DDTHH:mm:ssZ'
      ),
      endDateTime: date(prop('end_date_time', data), 'YYYY-MM-DDTHH:mm:ssZ'),
    }),
    [CATEGORY.INTERVIEW]: () => ({
      timeToRead: '13 min',
      description: toTextFromProp('description', data),
    }),
    [CATEGORY.RIGHT_NOW]: () => ({
      timeToRead: '5 min',
    }),
    [CATEGORY.CASE]: () => ({
      timeToRead: '2 min',
    }),
    [CATEGORY.LIFESPIRATION]: () => ({
      timeToRead: '2 min',
    }),
    [CATEGORY.KEYS_FIGURES]: () => ({
      timeToRead: '2 min',
      repereValue: toTextFromProp('repere_value', data),
      repereUnit: toTextFromProp('repere_unit', data),
      description: toTextFromProp('description', data),
    }),
    [CATEGORY.PORTRAIT]: () => ({
      timeToRead: '9 min',
      description: toTextFromProp('description', data),
    }),
    [CATEGORY.POINT_OF_VIEW]: () => ({
      timeToRead: '5 min',
    }),
    [CATEGORY.INNER_PAGE]: () => ({
      description: toTextFromProp('description', data),
    }),
    [CATEGORY.FOCUS]: () => ({
      timeToRead: '5 min',
      fiche: getSheet(data),
    }),
    [CATEGORY.SERVICES]: () => ({
      timeToRead: '5 min',
      fiche: getSheet(data, true),
    }),
  });
  return process ? process(data) : {};
};

const processArticleSlices = processSlices(commonSlices);

const PRISMIC_FILTERS = {
  Catégorie: 'category',
  Thématique: 'thematic',
  Territoire: 'territory',
  'Pas de suggestion': 'no-suggestion',
};

const getRelatedFilter = compose(
  propOr('category', __, PRISMIC_FILTERS),
  prop('filter_related_pushs')
);

// used in "filters" prop
const mapTypeFrom = (category) => {
  switch (category) {
    case CATEGORY.PUBLICATION:
      return CLASSIFICATION.PUBLICATIONS;
    case CATEGORY.EVENT:
      return CLASSIFICATION.EVENTS;
    case CATEGORY.SERVICES:
      return CLASSIFICATION.INNOVATION;
    case CATEGORY.FOCUS:
      return CLASSIFICATION.STARTUP;
    case CATEGORY.INNER_PAGE:
      return CLASSIFICATION.INNER_PAGE;
    default:
      return CLASSIFICATION.NEWS;
  }
};

/**
 * Transform the given input into an article content
 *
 * @param  {PrismicArticle} data
 * @return {ArticleSchema}
 */
export const processArticle = (entity) => {
  const data = prop('data', entity);
  const articleNormalized = compact({
    type: DocumentType.ARTICLE,
    breadcrumb: toTextFromProp('breadcrumb', data),
    subtitle: toTextFromProp('subtitle', data),
    category: toCategoryType(path('category', data)),
    classCategory: getCategoryClassName(data),
    classThematic: getThematicClassName('thematic', data),
    thematic: toThematicType(path('filters.0.thematic', data)),
    expertise: toExpertiseType(path('filters.0.expertise', data)),
    illustration: toImageFromProp('image_src', data),
    cta: prop('cta', data),
    author: prop('author', data),
    body: processArticleSlices(prop('body3', data)),
    relatedPushs: relatedPushs(data),
    relatedFilter: getRelatedFilter(data),
    filters: compact({
      [FILTER.CATEGORY]: toCategoryType(prop('category', data)),
      [FILTER.CLASSIFICATION]: mapTypeFrom(
        toCategoryType(prop('category', data))
      ),
      [FILTER.COMMON_THEMATIC]:
        toExpertiseType(path('filters.0.expertise', data)) ||
        toThematicType(path('filters.0.thematic', data)),
      [FILTER.EXPERTISE]: toExpertiseType(path('filters.0.expertise', data)),
      [FILTER.REGION]:
        toRegionFilterType(data?.filters[0]?.localization) ||
        toRegionFilterType(data?.filters[0]?.national) ||
        toRegionFilterType(data?.filters[0]?.international) ||
        regionTypeDefault(),
      [FILTER.THEMATIC]: toThematicType(path('filters.0.thematic', data)),
    }),
    localization:
      toRegionType(data?.filters[0]?.localization) ||
      toRegionType(data?.filters[0]?.national) ||
      toRegionType(data?.filters[0]?.international),
    onHome: eq('push_homepage', 'oui', true, data),
    homeCtaLabel: toTextFromProp('homepage_push_cta_label', data),
    ...processArticleProps(data),
    deprecated: [
      path('filters.0.thematic', data) &&
        !toThematicType(path('filters.0.thematic', data)) && {
          field: FILTER.THEMATIC,
          oldValue: path('filters.0.thematic', data),
        },
    ].filter(Boolean),
  });

  return articleNormalized;
};

/**
 * Normalizr schema representing an article
 */
export const ArticleEntity = deprecatedPageEntity(DocumentType.ARTICLE, {
  processStrategy: (entity) => ({
    ...processArticle(entity), // il faut passer entity direct sinon on n'a pas l'uid dans processArticle
  }),
});
